import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Progress } from 'components/ui/progress';
import { LocationStep } from './onboarding/location-step';
import { GenresStep } from './onboarding/genres-step';
import { SkillsStep } from './onboarding/skills-step';
import { WorkPreferenceStep } from './onboarding/work-preference-step';
import { useUpdateArtistProfile } from 'hooks/swrRequests/useUpdateArtistProfile';
import { WorkPreference } from 'constants/collab-club';
import { Artist } from 'types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OnboardingModalProps {
  profile: Artist;
  isOpen: boolean;
  onComplete: () => void;
}

interface FormData {
  location: string;
  genres: string[];
  skills: string[];
  workPreference: WorkPreference;
}

const STEPS = [
  { id: 'location', title: 'Location', description: 'Where are you based?' },
  { id: 'genres', title: 'Genres', description: 'What genres do you work in?' },
  { id: 'skills', title: 'Skills', description: 'What are your skills?' },
  { id: 'workPreference', title: 'Preference', description: 'What projects interest you?' },
];

export function OnboardingModal({ profile, isOpen, onComplete }: OnboardingModalProps) {
  const { t } = useTranslation('collab-club');
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    location: profile.location || '',
    genres: profile.genres || [],
    skills: profile.skills || [],
    workPreference: (profile.workPreference as WorkPreference) || 'both',
  });

  const { updateProfile, isLoading, error } = useUpdateArtistProfile();

  // Reset to first step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      // Reinitialize form data with current profile values
      setFormData({
        location: profile.location || '',
        genres: profile.genres || [],
        skills: profile.skills || [],
        workPreference: (profile.workPreference as WorkPreference) || 'both',
      });
    }
  }, [isOpen, profile]);

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // This is the last step, submit the form
      await handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const result = await updateProfile(profile.id, {
      ...formData,
      isOnboarded: true,
    });

    if (result.success) {
      onComplete();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Location
        return formData.location.length > 0;
      case 1: // Genres
        return formData.genres.length > 0;
      case 2: // Skills
        return formData.skills.length > 0;
      case 3: // Work Preference
        return formData.workPreference.length > 0;
      default:
        return true;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <LocationStep
            profile={profile}
            value={formData.location}
            onChange={(value) => updateFormData('location', value)}
          />
        );
      case 1:
        return (
          <GenresStep
            profile={profile}
            value={formData.genres}
            onChange={(value) => updateFormData('genres', value)}
          />
        );
      case 2:
        return (
          <SkillsStep
            profile={profile}
            value={formData.skills}
            onChange={(value) => updateFormData('skills', value)}
          />
        );
      case 3:
        return (
          <WorkPreferenceStep
            profile={profile}
            value={formData.workPreference}
            onChange={(value) => updateFormData('workPreference', value)}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal={true}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">{t('complete_profile')}</DialogTitle>
        </DialogHeader>

        {/* Step Content */}
        <div className="py-6">{renderStep()}</div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          {currentStep > 0 ? (
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-2 text-primary hover:text-primary bg-white hover:bg-gray-100 border border-primary"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('previous')}
            </Button>
          ) : (
            <div></div>
          )}
          <Button
            onClick={handleNext}
            disabled={!canProceed() || isLoading}
            className={`flex items-center gap-2 bg-blue-500 hover:bg-blue-600`}
          >
            {currentStep === STEPS.length - 1 ? t('finish') : t('next')}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
